/**
 * Mapping helpers for the Route Preview "board" (multi-technician map view).
 *
 * The route preview page has two consumers of the same stored routes:
 *  - The dealer cards + print path use the `DealerInfo` shape from `useRoutePreview`
 *    (which intentionally drops coordinates).
 *  - The map + drag-to-reorder need the raw `route_data.stops` with `lat`/`lng`.
 *
 * This module converts a stored `TechnicianRouteWithDetails` into the
 * coordinate-bearing `TechnicianRoute` shape that `RouteWithDirections` consumes,
 * plus the extra board metadata (id, status, start location) the page needs.
 */
import type {
  TechnicianRouteWithDetails,
  RouteData,
  RouteStop as DbRouteStop,
  RouteStartLocation,
  RouteStatus,
  RouteOptimization,
  OriginalOptimizedRoute
} from '@/types/technicianRoutes';
import type {
  TechnicianRoute as MapTechnicianRoute,
  RouteStop as MapRouteStop,
  DealerLocation
} from '../types';
import type { DirectionsResult } from './mapboxDirections';

/**
 * A technician route ready for the board: the map-facing `TechnicianRoute`
 * augmented with the persistence metadata the preview page needs.
 */
export interface BoardRoute extends MapTechnicianRoute {
  /** technician_routes.id (primary key) — used for cache + persistence */
  routeId: string;
  routeDate: string;
  status: RouteStatus;
  routeSequence: number;
  technicianName: string;
  /** Resolved start/home location for the map base location (may be null if 0/0). */
  startLocation: { lat: number; lng: number } | null;
  /** How the current stop order was produced ('manual' after a drag). */
  algorithm?: RouteOptimization['algorithm'];
  /** True when an optimized snapshot exists to revert to. */
  hasOriginalOptimized: boolean;
  /** Persisted driving geometry for static (non-focused) map rendering. */
  routeGeometry?: RouteData['route_geometry'];
}

/**
 * Stable dealer id for a stored stop. MUST match the derivation in
 * `useRoutePreview`'s `transformRouteStopToDealerInfo`, because the preview page
 * correlates board stops with the print/card `DealerInfo` list by this id.
 * (When `dealer_id` is empty — common for manual stops — both fall back to the
 * slugified dealer name.)
 */
export function dealerInfoId(
  stop: Pick<DbRouteStop, 'dealer_id' | 'dealer_name'> &
    Partial<Pick<DbRouteStop, 'stop_kind' | 'contact_id' | 'dealer_address' | 'address_line2'>>
): string {
  // Customer stops all share dealer_name 'Customer stop' and dealer_id '' — key by household.
  if (stop.stop_kind === 'customer') {
    if (stop.contact_id) return `customer-${stop.contact_id}`;
    return `customer-${slug(`${stop.dealer_address ?? ''} ${stop.address_line2 ?? ''}`)}`;
  }
  return stop.dealer_id || `dealer-${slug(stop.dealer_name)}`;
}

function slug(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, '-');
}

/**
 * Board-stop id: unique per stored stop. Embeds the ORIGINAL stop_order, which survives a
 * drag (the board renumbers stop_order but keeps id), so a reordered board stop can always
 * be matched back to the exact stored stop — even when several share a name.
 */
export function boardStopId(
  stop: Pick<DbRouteStop, 'dealer_id' | 'dealer_name' | 'stop_order'> &
    Partial<Pick<DbRouteStop, 'stop_kind' | 'contact_id' | 'dealer_address' | 'address_line2'>>
): string {
  const base =
    stop.stop_kind === 'customer' ? dealerInfoId(stop) : stop.dealer_id || stop.dealer_name;
  return `${base}-${stop.stop_order}`;
}

/** Convert a stored DB stop (snake_case, lat/lng) into the map RouteStop shape. */
function dbStopToMapStop(stop: DbRouteStop, skillLevel: number): MapRouteStop {
  const dealer: DealerLocation = {
    id: dealerInfoId(stop),
    name: stop.dealer_name,
    address: stop.dealer_address || '',
    city: stop.dealer_city || '',
    region: stop.dealer_region || '',
    postal_code: stop.dealer_postal_code || '',
    phone: stop.dealer_phone || '',
    latitude: stop.lat,
    longitude: stop.lng,
    recall_count: stop.recall_count || stop.recalls.length,
    skill_level: skillLevel
  };

  return {
    id: boardStopId(stop),
    dealer,
    arrival_time: stop.scheduled_arrival || '',
    departure_time: stop.scheduled_departure || '',
    service_duration: stop.estimated_service_duration_minutes || 30,
    stop_order: stop.stop_order,
    recalls: stop.recalls.map(r => ({
      vin: r.vin,
      campaign_number: r.campaign_number,
      recall_title: r.recall_title,
      ...(r.vehicle_year != null && { car_year: r.vehicle_year }),
      ...(r.vehicle_make && { make_name: r.vehicle_make }),
      ...(r.vehicle_model && { model_name: r.vehicle_model }),
      ...(r.stock_number && { stock_number: r.stock_number }),
      ...(stop.dealer_address && { dealer_address: stop.dealer_address }),
      ...(stop.dealer_city && { dealer_city: stop.dealer_city }),
      ...(stop.dealer_region && { dealer_region: stop.dealer_region }),
      ...(stop.dealer_postal_code && { dealer_postal_code: stop.dealer_postal_code }),
      ...(r.stop_type && { stop_type: r.stop_type }),
      ...(r.manual_stop_reason && { manual_stop_reason: r.manual_stop_reason }),
      ...(r.stop_kind && { stop_kind: r.stop_kind }),
      ...(r.contact_id && { contact_id: r.contact_id }),
      ...(r.dispatcher_note && { dispatcher_note: r.dispatcher_note })
    })),
    ...(stop.stop_type && { stop_type: stop.stop_type }),
    ...(stop.stop_kind && { stop_kind: stop.stop_kind }),
    ...(stop.contact_id && { contact_id: stop.contact_id }),
    ...(stop.address_line2 && { address_line2: stop.address_line2 })
  };
}

/** Resolve the map base location from a route's stored start_location. */
function resolveStartLocation(
  startLocation: RouteStartLocation | undefined
): { lat: number; lng: number } | null {
  if (
    startLocation &&
    typeof startLocation.lat === 'number' &&
    typeof startLocation.lng === 'number' &&
    startLocation.lat !== 0 &&
    startLocation.lng !== 0 &&
    !isNaN(startLocation.lat) &&
    !isNaN(startLocation.lng)
  ) {
    return { lat: startLocation.lat, lng: startLocation.lng };
  }
  return null;
}

/**
 * Map a stored route record into a `BoardRoute` for the map + reorder panel.
 * Stops are sorted by their persisted `stop_order` (single source of truth).
 *
 * @param record  The stored route from `get_routes_by_date_range`.
 * @param routeColor  The technician's color (from `getTechnicianColor`).
 */
export function storedRouteToBoardRoute(
  record: TechnicianRouteWithDetails,
  routeColor: string
): BoardRoute {
  const routeData = (record.route_data || {}) as RouteData;
  const stops = [...(routeData.stops || [])].sort(
    (a, b) => (a.stop_order || 0) - (b.stop_order || 0)
  );
  // Skill level is per-technician on the assignment side; routes don't carry it,
  // so use the first stop's value if present, else a neutral default.
  const skillLevel = 2;
  const startLocation = resolveStartLocation(routeData.start_location);

  return {
    routeId: record.id,
    routeDate: record.route_date,
    status: record.status,
    routeSequence: record.route_sequence || 1,
    technicianName: record.technician_name || 'Unknown',
    startLocation,
    ...(routeData.optimization?.algorithm && { algorithm: routeData.optimization.algorithm }),
    hasOriginalOptimized: !!routeData.original_optimized,
    ...(routeData.route_geometry && { routeGeometry: routeData.route_geometry }),
    technician_id: record.technician_id,
    technician: {
      id: record.technician_id,
      full_name: record.technician_name || 'Unknown',
      skill_level: skillLevel,
      phone_number: record.technician_phone || '',
      email: record.technician_email || '',
      ...(startLocation
        ? { home_base: { latitude: startLocation.lat, longitude: startLocation.lng } }
        : {})
    },
    stops: stops.map(s => dbStopToMapStop(s, skillLevel)),
    // Prefer the denormalized record totals (captured at assignment/sync time);
    // fall back to the optimization metadata stored in route_data.
    total_distance:
      record.total_distance_miles || routeData.optimization?.total_distance_miles || 0,
    total_duration:
      record.total_duration_minutes || routeData.optimization?.total_duration_minutes || 0,
    route_color: routeColor
  };
}

/**
 * Rebuild a persistable `RouteData` from a reordered list of board stops.
 * Preserves the original route_data (released_recalls, geometry, version, etc.)
 * and only rewrites the `stops` order + `optimization` metadata.
 *
 * @param original     The original stored route_data (single source of truth).
 * @param orderedStops The board stops in their new visual order.
 * @param optimizedAt  ISO timestamp for the optimization metadata.
 * @param algorithm    Which algorithm produced this order ('manual' for drag).
 */
export function buildReorderedRouteData(
  original: RouteData,
  orderedStops: MapRouteStop[],
  optimizedAt: string,
  algorithm: RouteOptimization['algorithm'] = 'manual'
): RouteData {
  // Index original DB stops by their board id (unique per stop — see boardStopId) so we
  // keep their full detail (recalls, leg distances) and only change ordering. Matching by
  // dealer_id/name lost data: every customer stop is 'Customer stop' with dealer_id '', so
  // all of them resolved to the same stored stop and the others' recalls were dropped.
  const byBoardId = new Map<string, DbRouteStop>();
  const byLegacyKey = new Map<string, DbRouteStop>();
  (original.stops || []).forEach(s => {
    byBoardId.set(boardStopId(s), s);
    if (s.stop_kind !== 'customer') byLegacyKey.set(s.dealer_id || s.dealer_name, s);
  });

  const stops: DbRouteStop[] = orderedStops.map((mapStop, idx) => {
    const dbStop =
      byBoardId.get(mapStop.id) ||
      (mapStop.stop_kind === 'customer'
        ? undefined
        : byLegacyKey.get(mapStop.dealer.id) || byLegacyKey.get(mapStop.dealer.name));
    if (dbStop) {
      return { ...dbStop, stop_order: idx + 1 };
    }
    // Fallback: synthesize a DB stop from the map stop (shouldn't normally happen)
    return {
      stop_order: idx + 1,
      dealer_id: mapStop.dealer.id,
      dealer_name: mapStop.dealer.name,
      dealer_address: mapStop.dealer.address,
      dealer_city: mapStop.dealer.city,
      dealer_region: mapStop.dealer.region,
      dealer_postal_code: mapStop.dealer.postal_code,
      dealer_phone: mapStop.dealer.phone,
      lat: mapStop.dealer.latitude,
      lng: mapStop.dealer.longitude,
      recalls: [],
      recall_count: mapStop.dealer.recall_count,
      distance_from_previous_miles: 0,
      duration_from_previous_minutes: 0,
      ...(mapStop.stop_type && { stop_type: mapStop.stop_type })
    };
  });

  return {
    ...original,
    stops,
    optimization: {
      ...(original.optimization || {}),
      algorithm,
      optimized_at: optimizedAt
    }
  };
}

/**
 * Merge a Directions/Optimization recompute into a RouteData: writes the
 * refreshed stops (with their per-leg metrics), the trip totals, and the map
 * geometry. Used after a manual reorder (algorithm 'manual') or a re-optimize.
 */
export function applyDirectionsToRouteData(
  base: RouteData,
  result: Pick<
    DirectionsResult,
    'stops' | 'totalDistanceMiles' | 'totalDurationMinutes' | 'geometry'
  >,
  algorithm: RouteOptimization['algorithm'],
  optimizedAt: string
): RouteData {
  return {
    ...base,
    stops: result.stops,
    optimization: {
      ...(base.optimization || {}),
      algorithm,
      optimized_at: optimizedAt,
      total_distance_miles: result.totalDistanceMiles,
      total_duration_minutes: result.totalDurationMinutes
    },
    ...(result.geometry ? { route_geometry: result.geometry } : {})
  };
}

/**
 * Snapshot the current (optimized) state of a route so it can be reverted to
 * after manual edits. Call this with the pre-edit route_data.
 */
export function snapshotOptimized(routeData: RouteData): OriginalOptimizedRoute {
  return {
    stops: routeData.stops,
    total_distance_miles: routeData.optimization?.total_distance_miles ?? 0,
    total_duration_minutes: routeData.optimization?.total_duration_minutes ?? 0,
    ...(routeData.route_geometry ? { route_geometry: routeData.route_geometry } : {}),
    algorithm: routeData.optimization?.algorithm ?? 'mapbox_optimization',
    optimized_at: routeData.optimization?.optimized_at ?? new Date().toISOString()
  };
}

/**
 * Restore a route's stored optimized snapshot. Returns null when no snapshot
 * exists. Keeps `original_optimized` in place so the user can re-revert later.
 */
export function restoreOptimizedRouteData(routeData: RouteData): RouteData | null {
  const snap = routeData.original_optimized;
  if (!snap) return null;
  return {
    ...routeData,
    stops: snap.stops,
    optimization: {
      ...(routeData.optimization || {}),
      algorithm: snap.algorithm,
      optimized_at: snap.optimized_at,
      total_distance_miles: snap.total_distance_miles,
      total_duration_minutes: snap.total_duration_minutes
    },
    ...(snap.route_geometry ? { route_geometry: snap.route_geometry } : {})
  };
}
